import { z } from 'zod';
import { redigierePfad } from '../korrelation';
/**
 * Diagnostic context of a user-submitted report (DF-4 — the bouwsteen; the
 * feedback loop's intake consumes it in DF-8/DF-9).
 *
 * WHAT THIS IS: the `.strict()` shape of the `kontext` JSONB an Eingabe may
 * carry (DRIP-FEEDBACK-LOOP.md §6.2). ADR-052's last sentence is the law here:
 * nothing is attached that the melder cannot see, and NOTHING that is not in
 * this schema can ride along — `.strict()` refuses unknown keys instead of
 * stripping them, so a widened client cannot quietly widen the stored context
 * (invariant D-D: free text lives in exactly one place, and it is not here).
 *
 * WHAT IS DELIBERATELY NOT IN IT, because the datamodel gives each its own
 * column with its own CHECK: `umgebung` (staging|produktion — required the
 * moment a kontext exists, `drip_eingabe_kontext_hat_umgebung`) and
 * `artefaktRef` ({kind, value} — `drip_eingabe_artefakt_form`). A copy inside
 * the JSONB would be a second place for the same fact.
 *
 * NAMING: product-neutral on purpose. `packages/shared` is core vocabulary and
 * travels into every commercial extraction (§5.3 of the ontwerp); a schema
 * that said "drip" in its name would put the internal steering instrument by
 * name into a sold package. Diagnostic context of a report is a neutral
 * concept; that DRIP is today's only consumer is product knowledge.
 *
 * NOT named `*Update*`/`*Patch*` and carrying NO `.default()` — both by
 * design: the zod4 default-leak guard watches those names because defaults
 * leak into PATCHes; this schema describes a snapshot, so a default would
 * invent a fact about the melder's browser that was never measured.
 */
/**
 * Which application the report was made from. `mitwirkung` is the external
 * surface of the feedback loop (routegroep, DF-9); the other three exist
 * today. Core vocabulary like `PORTAL_SURFACES` — the names are the wire
 * contract, the products behind them are product knowledge.
 */
export const DIAGNOSE_ANWENDUNGEN = ['crm', 'portal', 'verwaltung', 'mitwirkung'];
/**
 * The correlation id as the backend mints and validates it
 * (`apps/backend/src/common/correlation.ts`, UUID_RE): a UUID, any RFC
 * version 1-8, case-insensitive. The pattern is repeated here because shared
 * is node-free and cannot import backend code — the diagnose-kontext test
 * exercises both directions so the two copies cannot drift silently.
 */
export const KORRELATIONS_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
/**
 * One viewport measurement. Bounded: a viewport is a screen, not a payload —
 * and `int` because half pixels are a rendering concept, not a CSS one.
 */
export const diagnoseViewportSchema = z
    .object({
    breite: z.number().int().min(1).max(100_000),
    hoehe: z.number().int().min(1).max(100_000),
})
    .strict();
/**
 * The kontext blob itself. Every field optional — the melder may strike any
 * block before sending (invariant D-E: shown before sending, removable per
 * block) — but what IS sent must fit exactly.
 *
 * `browserklasse` is the COARSENED user agent ("Chrome/Windows"), never the
 * raw string: the raw UA is a fingerprint, the class is a repro hint. The
 * coarsening lives next to the ringbuffer in `korrelation.ts`.
 *
 * `route` IS REDACTED BY THE SCHEMA ITSELF (review #430, minor 1). It used to
 * be redacted only where it was COLLECTED — `sammleDiagnoseKontext` runs
 * `redigierePfad` over `location.pathname` — which made the promise a property
 * of one client rather than of the stored fact. Measured on the intake: a
 * request carrying `route: '/mitwirkung/abnahme?token=…&sid=…'` was accepted
 * verbatim and FROZEN into `drip_eingaben.kontext` by the submission trigger,
 * so a live single-use token landed in a row that can never be edited again.
 * A `.transform` here is the one place both sides pass through, and it is
 * idempotent — an already-redacted path from the honest collector comes out
 * unchanged, so nothing about the client path changes.
 */
export const diagnoseKontextSchema = z
    .object({
    /**
     * `window.location.pathname` — deeplinks are stable (§6.2). Redacted on
     * parse: query string stripped, opaque segments replaced (the SAME
     * `redigierePfad` the ringbuffer uses, with exactly the bounds its
     * docstring states).
     */
    route: z.string().min(1).max(500).transform(redigierePfad).optional(),
    anwendung: z.enum(DIAGNOSE_ANWENDUNGEN).optional(),
    /** Last correlation id seen by the API client (the ringbuffer's head). */
    correlationId: z.string().regex(KORRELATIONS_ID_RE).optional(),
    viewport: diagnoseViewportSchema.optional(),
    browserklasse: z.string().min(1).max(100).optional(),
})
    .strict();
